import { isApiError } from '../index';
import { useErrorMessage } from '../index';
import { useConnectionStore } from '../index';
import type { UseQueryResult } from '@tanstack/react-query';
import { Button, ErrorState, Skeleton } from '@sentinel/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface QueryStateProps<T> {
  query: UseQueryResult<T>;
  skeletonLines?: number;
  children: (data: T) => ReactNode;
}

/** Every data-driven screen handles loading, error, offline and access denied here (§11). */
export function QueryState<T>({ query, skeletonLines = 4, children }: QueryStateProps<T>) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const online = useConnectionStore((s) => s.browserOnline);

  if (query.isPending) {
    return (
      <div className="panel-b" aria-busy="true">
        <Skeleton lines={skeletonLines} />
      </div>
    );
  }
  if (query.isError) {
    const denied = isApiError(query.error) && query.error.category === 'authorization';
    const offline = !online || (isApiError(query.error) && query.error.category === 'connection');
    const title = denied
      ? t('common.states.deniedTitle')
      : offline
        ? t('common.states.offlineTitle')
        : t('common.states.errorTitle');
    const description = denied
      ? t('common.states.deniedDescription')
      : offline
        ? t('common.states.offlineDescription')
        : errorMessage(query.error);
    const requestId = isApiError(query.error) ? query.error.requestId : null;
    return (
      <ErrorState
        title={title}
        description={
          requestId === null
            ? description
            : `${description} ${t('common.states.requestId')}: ${requestId}`
        }
        action={
          denied ? undefined : (
            <Button size="sm" onClick={() => void query.refetch()}>
              {t('common.actions.retry')}
            </Button>
          )
        }
      />
    );
  }
  return <>{children(query.data)}</>;
}
